import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Building2, Search, Phone, Mail, FileText, Download, ChevronDown, ChevronUp, MapPin, Eye, Trash2,
  Globe, User, Briefcase, CheckCircle, XCircle, Camera, Anchor, Upload, Send, X, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { openBlobInNewTab } from '../utils/fileUtils';
import RoleBadges from '../components/RoleBadges';

const ListeEntreprises = ({ user }) => {
  const [entreprises, setEntreprises] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEntreprise, setExpandedEntreprise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(null);
  const [uploadData, setUploadData] = useState({ titre: '', description: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [centreDocuments, setCentreDocuments] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadEntreprises();
  }, []);

  const loadEntreprises = async () => {
    try {
      const response = await axios.get('/entreprises');
      // Afficher immédiatement sans enrichissement lourd
      setEntreprises((response.data || []).map(e => ({ ...e, documents: [] })));
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Charger profil + documents uniquement quand on déplie une entreprise
  const loadEntrepriseDetails = async (userId) => {
    try {
      const [profileRes, docsRes, docsEntrepriseRes] = await Promise.all([
        axios.get(`/entreprises/${userId}`).catch(() => ({ data: {} })),
        axios.get(`/documents/${userId}`).catch(() => ({ data: [] })),
        axios.get(`/documents/entreprise/${userId}`).catch(() => ({ data: [] }))
      ]);
      const profile = profileRes.data?.profile || {};
      const allDocs = [...(docsRes.data || []), ...(docsEntrepriseRes.data || [])];
      const uniqueDocs = allDocs.filter((doc, index, self) => 
        index === self.findIndex(d => d.document_id === doc.document_id)
      );
      setEntreprises(prev => prev.map(e => {
        if (e.user_id === userId) {
          return { 
            ...e,
            nom_entreprise: profile.nom || profile.nom_entreprise || e.user_info?.nom || e.nom || '',
            adresse: profile.adresse || e.adresse || '',
            code_postal: profile.code_postal || '',
            ville: profile.ville || '',
            telephone: profile.telephone || e.user_info?.telephone || '',
            email: profile.email || e.user_info?.email || '',
            logo: profile.logo || '',
            siret: profile.siret || '',
            activites: profile.activites || {},
            gerant_nom: profile.gerant_nom || '',
            gerant_prenom: profile.gerant_prenom || '',
            gerant_genre: profile.gerant_genre || '',
            gerant_email: profile.gerant_email || '',
            gerant_telephone: profile.gerant_telephone || '',
            documents: uniqueDocs,
            _loaded: true
          };
        }
        return e;
      }));
    } catch (e) {
      // Silently fail
    }
  };

  // Voir un document
  const viewDocument = async (documentId) => {
    try {
      toast.info('Chargement du document...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/pdf' });
        openBlobInNewTab(blob);
      } else {
        toast.error('Document non disponible');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ouverture du document');
    }
  };

  // Télécharger un document
  const downloadDocument = async (documentId, filename) => {
    try {
      toast.info('Téléchargement...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || doc.filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Document téléchargé');
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Supprimer un document
  const deleteDocument = async (documentId, entrepriseId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ? Cette action supprimera également le document de l\'espace personnel de l\'entreprise.')) {
      return;
    }
    try {
      await axios.delete(`/documents/${documentId}`);
      toast.success('Document supprimé');
      setEntreprises(prev => prev.map(e => {
        if (e.user_id === entrepriseId) {
          return { ...e, documents: e.documents.filter(d => d.document_id !== documentId) };
        }
        return e;
      }));
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Supprimer une entreprise
  const deleteEntreprise = async (entrepriseId, entrepriseNom) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'entreprise ${entrepriseNom} ? Cette action est irréversible.`)) {
      return;
    }
    try {
      await axios.delete(`/entreprises/${entrepriseId}`);
      toast.success('Entreprise supprimée');
      setEntreprises(prev => prev.filter(e => e.user_id !== entrepriseId));
    } catch (error) {
      toast.error('Erreur lors de la suppression de l\'entreprise');
    }
  };

  const loadCentreDocuments = async (entrepriseId) => {
    try {
      const response = await axios.get(`/documents-entreprise/${entrepriseId}`);
      setCentreDocuments(prev => ({ ...prev, [entrepriseId]: response.data || [] }));
    } catch (error) {
      setCentreDocuments(prev => ({ ...prev, [entrepriseId]: [] }));
    }
  };

  const handleUploadToEntreprise = async (entrepriseId) => {
    if (!uploadData.titre.trim()) { toast.error('Le titre est requis'); return; }
    setUploading(true);
    const formData = new FormData();
    if (selectedFile) formData.append('file', selectedFile);
    formData.append('destinataire_id', entrepriseId);
    formData.append('titre', uploadData.titre);
    formData.append('description', uploadData.description || '');
    try {
      await axios.post('/documents-entreprise/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(selectedFile ? 'Document envoyé' : 'Message envoyé');
      setShowUploadModal(null); setSelectedFile(null); setUploadData({ titre: '', description: '' });
      loadCentreDocuments(entrepriseId);
    } catch (error) { toast.error('Erreur'); }
    finally { setUploading(false); }
  };

  const viewCentreDoc = async (doc) => {
    try {
      const response = await axios.get(`/documents-entreprise/${doc.document_id}/download`, { responseType: 'blob' });
      openBlobInNewTab(new Blob([response.data], { type: doc.content_type || 'application/pdf' }));
    } catch (error) { toast.error('Erreur'); }
  };

  const downloadCentreDoc = async (doc) => {
    try {
      const response = await axios.get(`/documents-entreprise/${doc.document_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', doc.filename); document.body.appendChild(link); link.click(); link.remove();
    } catch (error) { toast.error('Erreur'); }
  };

  const deleteCentreDoc = async (documentId, entrepriseId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`/documents-entreprise/${documentId}`);
      toast.success('Document supprimé'); loadCentreDocuments(entrepriseId);
    } catch (error) { toast.error('Erreur'); }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  const filteredEntreprises = entreprises.filter(e => {
    const nom = e.nom_entreprise || e.nom || e.user_info?.nom || '';
    const adresse = e.adresse || '';
    const email = e.email || e.user_info?.email || '';
    return `${nom} ${adresse} ${email}`.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-display font-bold text-[#0F172A]">Entreprises</h1>
        <p className="text-slate-600">Profils complets et documents des entreprises partenaires</p>
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          placeholder="Rechercher une entreprise..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
          data-testid="search-entreprise"
        />
      </div>

      {/* Stats */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-4">
        <Building2 className="h-8 w-8 text-orange-600" />
        <div>
          <p className="text-2xl font-bold text-orange-700">{entreprises.length}</p>
          <p className="text-sm text-orange-600">Entreprises partenaires</p>
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-4">
        {filteredEntreprises.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucune entreprise trouvée</p>
          </div>
        ) : (
          filteredEntreprises.map((entreprise) => {
            const isExpanded = expandedEntreprise === entreprise.user_id;
            const nom = entreprise.nom_entreprise || entreprise.nom || entreprise.user_info?.nom || 'Entreprise';
            const activites = entreprise.activites || {};
            
            return (
              <div
                key={entreprise.user_id}
                className="bg-white border border-slate-200 rounded-xl hover:shadow-lg transition-all"
                data-testid={`entreprise-${entreprise.user_id}`}
              >
                {/* En-tête */}
                <div 
                  className="p-4 cursor-pointer hover:bg-slate-50"
                  onClick={() => {
                    const newId = isExpanded ? null : entreprise.user_id;
                    setExpandedEntreprise(newId);
                    if (newId) {
                      if (!entreprise._loaded) loadEntrepriseDetails(newId);
                      if (!centreDocuments[newId]) loadCentreDocuments(newId);
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Logo */}
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center text-white text-xl font-bold">
                        {entreprise.logo ? (
                          <img src={entreprise.logo} alt={nom} className="w-full h-full object-cover" />
                        ) : (
                          nom.substring(0, 2).toUpperCase()
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-[#0F172A] flex items-center gap-2 flex-wrap">
                          {nom}
                          {entreprise.is_secondary_structure && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium" title={`Structure secondaire de ${entreprise.owner_info?.prenom || ''} ${entreprise.owner_info?.nom || ''}`}>
                              Structure secondaire
                            </span>
                          )}
                        </h3>
                        {entreprise.owner_info && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            Propriétaire : {entreprise.owner_info.prenom} {entreprise.owner_info.nom} ({entreprise.owner_info.email})
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          {entreprise.adresse && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" /> {entreprise.ville || entreprise.adresse}
                            </span>
                          )}
                          {entreprise.telephone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {entreprise.telephone}
                            </span>
                          )}
                        </div>
                        {user?.role === 'centre' && (
                          <RoleBadges userId={entreprise.user_id} roles={entreprise.user_info?.roles || entreprise.roles || ['entreprise']} onRolesChange={(newRoles) => {
                            setEntreprises(prev => prev.map(e => e.user_id === entreprise.user_id ? {...e, roles: newRoles, user_info: {...e.user_info, roles: newRoles}} : e));
                          }} />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                        {entreprise.documents?.length || 0} document(s)
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteEntreprise(entreprise.user_id, nom);
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer l'entreprise"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Détails dépliés */}
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-gradient-to-b from-orange-50 to-white p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      
                      {/* Colonne 1: Profil entreprise */}
                      <div className="bg-white p-5 rounded-xl border border-orange-200 shadow-sm">
                        <h4 className="font-semibold text-orange-800 mb-4 flex items-center gap-2">
                          <Building2 className="h-5 w-5" />
                          Profil Entreprise
                        </h4>
                        
                        {/* Logo grande */}
                        <div className="flex justify-center mb-4">
                          <div className="w-24 h-24 rounded-xl overflow-hidden bg-orange-100 border-4 border-orange-200">
                            {entreprise.logo ? (
                              <img src={entreprise.logo} alt={nom} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-orange-400">
                                <Camera className="h-8 w-8" />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Nom</span>
                            <span className="font-medium">{nom}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Adresse</span>
                            <span className="font-medium text-right text-xs">{entreprise.adresse || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Code postal</span>
                            <span className="font-medium">{entreprise.code_postal || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Ville</span>
                            <span className="font-medium">{entreprise.ville || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Téléphone</span>
                            <span className="font-medium">{entreprise.telephone || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Email</span>
                            <span className="font-medium text-xs">{entreprise.email || entreprise.user_info?.email || '-'}</span>
                          </div>
                        </div>

                        {/* Gérant */}
                        {(entreprise.gerant_nom || entreprise.gerant_prenom) && (
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <h5 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                              <User className="h-4 w-4" />
                              Gérant
                            </h5>
                            <div className="space-y-2 text-sm">
                              <p><span className="text-slate-500">Nom:</span> {entreprise.gerant_genre || ''} {entreprise.gerant_prenom || ''} {entreprise.gerant_nom || '-'}</p>
                              <p><span className="text-slate-500">Email:</span> {entreprise.gerant_email || '-'}</p>
                              <p><span className="text-slate-500">Tél:</span> {entreprise.gerant_telephone || '-'}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Colonne 2: Activités */}
                      <div className="bg-white p-5 rounded-xl border border-orange-200 shadow-sm">
                        <h4 className="font-semibold text-orange-800 mb-4 flex items-center gap-2">
                          <Anchor className="h-5 w-5" />
                          Activités Nautiques
                        </h4>
                        
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm">Randonnée VNM</span>
                            {activites.randonnee_vnm ? (
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-5 w-5 text-green-500" />
                                {activites.nombre_vnm > 0 && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                    {activites.nombre_vnm} VNM
                                  </span>
                                )}
                              </div>
                            ) : (
                              <XCircle className="h-5 w-5 text-slate-300" />
                            )}
                          </div>
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm">Engins tractés</span>
                            {activites.engins_tractes ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-slate-300" />
                            )}
                          </div>
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm">PNM</span>
                            {activites.pnm ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-slate-300" />
                            )}
                          </div>
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm">Flyboard</span>
                            {activites.flyboard ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-slate-300" />
                            )}
                          </div>
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm">Location bateau</span>
                            {activites.location_bateau ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-slate-300" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Colonne 3: Documents */}
                      <div className="bg-white p-5 rounded-xl border border-orange-200 shadow-sm">
                        <h4 className="font-semibold text-orange-800 mb-4 flex items-center gap-2">
                          <FileText className="h-5 w-5" />
                          Documents ({entreprise.documents?.length || 0})
                        </h4>
                        {(!entreprise.documents || entreprise.documents.length === 0) ? (
                          <div className="text-center py-8 bg-slate-50 rounded-lg">
                            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-500 text-sm">Aucun document uploadé</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {entreprise.documents.map((doc, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-100">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-orange-600" />
                                  <span className="text-sm font-medium text-orange-800 truncate max-w-[120px]">
                                    {doc.document_type || doc.type || 'Document'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => viewDocument(doc.document_id)}
                                    className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                                    title="Voir"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => downloadDocument(doc.document_id, `${doc.document_type || 'doc'}_${nom}`)}
                                    className="p-2 text-green-500 hover:bg-green-100 rounded-lg transition-colors"
                                    title="Télécharger"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteDocument(doc.document_id, entreprise.user_id)}
                                    className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Section Documents envoyés par le centre */}
                    <div className="mt-4 bg-white p-3 sm:p-5 rounded-xl border border-[#8DC63F]/30 shadow-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                        <h4 className="font-semibold text-[#0F172A] text-sm flex items-center gap-2">
                          <Send className="h-4 w-4 text-[#8DC63F] flex-shrink-0" />
                          <span className="truncate">Docs centre ({(centreDocuments[entreprise.user_id] || []).length})</span>
                        </h4>
                        <Button size="sm" onClick={() => setShowUploadModal(entreprise.user_id)} className="bg-[#8DC63F] hover:bg-[#7db336] text-xs whitespace-nowrap">
                          <Upload className="h-3.5 w-3.5 mr-1" /> Envoyer
                        </Button>
                      </div>
                      {(centreDocuments[entreprise.user_id] || []).length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">Aucun document ou message envoyé</p>
                      ) : (
                        <div className="space-y-2">
                          {(centreDocuments[entreprise.user_id] || []).map(doc => (
                            <div key={doc.document_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                              <div className="flex items-center gap-3">
                                {doc.type === 'message' ? <MessageSquare className="h-4 w-4 text-blue-500" /> : <FileText className="h-4 w-4 text-[#8DC63F]" />}
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-[#0F172A]">{doc.titre}</span>
                                    {doc.type === 'message' && <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">Message</span>}
                                  </div>
                                  {doc.description && <p className="text-xs text-slate-600">{doc.description}</p>}
                                  <div className="flex items-center gap-2 text-xs text-slate-400">
                                    {doc.filename && <span>{doc.filename}</span>}
                                    {doc.file_size && <span>{formatFileSize(doc.file_size)}</span>}
                                    <span>{new Date(doc.created_at).toLocaleDateString('fr-FR')}</span>
                                    {doc.lu && <span className="text-green-600 font-medium">Lu</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {doc.type !== 'message' && (
                                  <>
                                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => viewCentreDoc(doc)}><Eye className="h-3 w-3" /></Button>
                                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => downloadCentreDoc(doc)}><Download className="h-3 w-3" /></Button>
                                  </>
                                )}
                                <Button variant="outline" size="sm" className="h-7 text-xs text-red-600 border-red-200" onClick={() => deleteCentreDoc(doc.document_id, entreprise.user_id)}><Trash2 className="h-3 w-3" /></Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Modal Upload Document / Message */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0F172A]">Envoyer un document ou message</h3>
              <button onClick={() => { setShowUploadModal(null); setSelectedFile(null); setUploadData({ titre: '', description: '' }); }} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Titre *</label>
                <input type="text" value={uploadData.titre} onChange={(e) => setUploadData({ ...uploadData, titre: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#8DC63F]" placeholder="Ex: Convention, Attestation..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message / Description</label>
                <textarea value={uploadData.description} onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-lg resize-none" placeholder="Saisissez votre message ici..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fichier (optionnel)</label>
                <input type="file" ref={fileInputRef} onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} className="hidden" />
                <div onClick={() => fileInputRef.current?.click()} className="w-full border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#8DC63F]">
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="h-5 w-5 text-[#8DC63F]" />
                      <span className="text-sm">{selectedFile.name}</span>
                      <button onClick={(e) => { e.stopPropagation(); setSelectedFile(null); if(fileInputRef.current) fileInputRef.current.value = ''; }} className="ml-1 text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <div><Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-500">Cliquez pour sélectionner (optionnel)</p></div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => { setShowUploadModal(null); setSelectedFile(null); }} className="flex-1">Annuler</Button>
              <Button onClick={() => handleUploadToEntreprise(showUploadModal)} disabled={uploading || !uploadData.titre.trim()} className="flex-1 bg-[#8DC63F] hover:bg-[#7db336]" data-testid="send-doc-message-btn">
                {uploading ? 'Envoi...' : <><Send className="h-4 w-4 mr-2" /> Envoyer</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListeEntreprises;
